/**
 * ticketPulse — تشخیص «پیام جدید تیکت» با polling سبک از خودِ وب‌سرویس.
 *
 * جایگزین کانال Firebase RTDB (که به‌خاطر قطعی/فیلتر حذف شد). هر ۳۰ ثانیه
 * `GET CRM/TicketsPulse` را می‌زند و با عکس قبلی مقایسه می‌کند؛ اگر شمار پیام‌های
 * یک تیکت زیاد شده یا وضعیتش «دیده‌نشده» شده باشد، آن تیکت به فهرست هشدارها
 * افزوده می‌شود. هشدار تا وقتی کاربر خودش ببندد (یا تیکت را باز کند) باقی می‌ماند.
 *
 * فقط وقتی صفحه‌ی فعال زیر مسیرهای تیکت است فعال می‌شود (acquire/release).
 */
import { reactive } from "vue";
import { createLivePoll } from "@/utils/livePoll";
import crmApi from "@/api/crmApi";

export const pulseState = reactive({
    /** [{ ticketId, title, messageCount }] — تیکت‌هایی که پیام جدید دارند و هنوز بسته نشده‌اند. */
    alerts: [],
});

let snapshot = null; // Map<ticketId, { messageCount, unseen }>
let poll = null;
let refCount = 0;

function upsertAlert(ticketId, messageCount) {
    const existing = pulseState.alerts.find((a) => a.ticketId === ticketId);
    if (existing) {
        existing.messageCount = messageCount;
    } else {
        pulseState.alerts.push({ ticketId, messageCount });
    }
}

async function tick() {
    const res = await crmApi.getTicketsPulse();
    const list = (res && res.data && res.data.Tickets) || [];
    const next = new Map();

    const firstRun = snapshot === null;

    list.forEach((t) => {
        const id = t.TicketId;
        const messageCount = t.MessageCount || 0;
        const unseen = !!t.Unseen;
        next.set(id, { messageCount, unseen });

        const prev = snapshot && snapshot.get(id);

        const grew = prev ? messageCount > prev.messageCount : false;
        const becameUnseen = prev ? unseen && !prev.unseen : false;
        // در اولین اجرا، تیکتِ از قبل دیده‌نشده هم به‌عنوان «پیام خوانده‌نشده» نشان داده می‌شود.
        const firstRunUnseen = firstRun && unseen;

        if (grew || becameUnseen || firstRunUnseen) {
            upsertAlert(id, messageCount);
        }
    });

    snapshot = next;
}

export function acquireTicketPulse() {
    refCount += 1;
    if (!poll) {
        poll = createLivePoll(tick, { interval: 30000, minInterval: 10000, maxInterval: 120000 });
        poll.start();
    }
    return releaseTicketPulse;
}

export function releaseTicketPulse() {
    refCount = Math.max(0, refCount - 1);
    if (refCount === 0 && poll) {
        poll.stop();
        poll = null;
    }
}

/** کاربر تیکت را باز کرد/دید → هشدارش را بردار و عکس را هم‌تراز کن تا دوباره هشدار ندهد. */
export function markTicketViewed(ticketId, messageCount) {
    const i = pulseState.alerts.findIndex((a) => a.ticketId === ticketId);
    if (i !== -1) pulseState.alerts.splice(i, 1);

    if (snapshot && snapshot.has(ticketId)) {
        const cur = snapshot.get(ticketId);
        snapshot.set(ticketId, {
            messageCount: typeof messageCount === "number" ? messageCount : cur.messageCount,
            unseen: false,
        });
    }
}

export function dismissTicketAlert(ticketId) {
    const i = pulseState.alerts.findIndex((a) => a.ticketId === ticketId);
    if (i !== -1) pulseState.alerts.splice(i, 1);
}

export function dismissAllTicketAlerts() {
    pulseState.alerts.splice(0, pulseState.alerts.length);
}

/** تازه‌سازی فوری (مثلاً بعد از ارسال پاسخ توسط کاربر). */
export function pokeTicketPulse() {
    if (poll) poll.poke();
}

export default {
    pulseState,
    acquireTicketPulse,
    releaseTicketPulse,
    markTicketViewed,
    dismissTicketAlert,
    dismissAllTicketAlerts,
    pokeTicketPulse,
};
